// src/modules/Admin/pages/Products/index.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSearch,
  FaEye,
  FaEyeSlash,
  FaStar,
  FaRegStar,
  FaBoxOpen
} from 'react-icons/fa';
import { supabase } from '../../../../config/supabase';

const PageContainer = styled.div``;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;

  h1 {
    font-size: 24px;
    font-weight: 600;
    color: #111;
    display: flex;
    align-items: center;
    gap: 12px;

    svg {
      color: #00D4AA;
    }
  }
`;

const AddButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #00D4AA;
  color: #000;
  padding: 12px 20px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  transition: all 200ms;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const SearchBox = styled.div`
  flex: 1;
  min-width: 250px;
  max-width: 400px;
  position: relative;

  input {
    width: 100%;
    padding: 12px 16px 12px 44px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 14px;

    &:focus {
      outline: none;
      border-color: #00D4AA;
    }
  }

  svg {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #999;
  }
`;

const FilterSelect = styled.select`
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  min-width: 150px;

  &:focus {
    outline: none;
    border-color: #00D4AA;
  }
`;

const Table = styled.table`
  width: 100%;
  background: white;
  border-radius: 12px;
  border: 1px solid #eee;
  border-collapse: collapse;
  overflow: hidden;
`;

const Th = styled.th`
  text-align: left;
  padding: 16px;
  background: #f8f8f8;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  border-bottom: 1px solid #eee;
`;

const Td = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #333;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
`;

const ProductInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ProductImage = styled.img`
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 8px;
  background: #f5f5f5;
`;

const ProductName = styled.span`
  font-weight: 500;
  color: #111;
`;

const ProductSku = styled.span`
  font-size: 12px;
  color: #999;
  display: block;
`;

const Price = styled.span`
  font-weight: 600;
  color: #111;
`;

const Stock = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.$low ? 'rgba(239, 68, 68, 0.1)' : 'rgba(34, 197, 94, 0.1)'};
  color: ${props => props.$low ? '#ef4444' : '#22c55e'};
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.$active ? 'rgba(34, 197, 94, 0.1)' : 'rgba(156, 163, 175, 0.1)'};
  color: ${props => props.$active ? '#22c55e' : '#6b7280'};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #eee;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 200ms;
  color: ${props => props.$danger ? '#ef4444' : props.$warning ? '#f59e0b' : '#666'};

  &:hover {
    border-color: ${props => props.$danger ? '#ef4444' : props.$warning ? '#f59e0b' : '#00D4AA'};
    background: ${props => props.$danger ? 'rgba(239, 68, 68, 0.05)' : props.$warning ? 'rgba(245, 158, 11, 0.05)' : 'rgba(0, 212, 170, 0.05)'};
    color: ${props => props.$danger ? '#ef4444' : props.$warning ? '#f59e0b' : '#00D4AA'};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 64px 24px;
  background: white;
  border-radius: 12px;
  border: 1px solid #eee;

  svg {
    font-size: 48px;
    color: #ddd;
    margin-bottom: 16px;
  }

  h3 {
    font-size: 18px;
    color: #333;
    margin-bottom: 8px;
  }

  p {
    color: #666;
    margin-bottom: 24px;
  }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: white;
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 20px;

  h4 {
    font-size: 12px;
    color: #666;
    margin-bottom: 8px;
    text-transform: uppercase;
  }

  p {
    font-size: 28px;
    font-weight: 700;
    color: #111;
  }
`;

const formatPrice = (price) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(price);
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    outOfStock: 0,
    featured: 0
  });

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [search, categoryFilter, statusFilter]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('shop_products')
        .select('*, category:shop_categories(name, slug)')
        .order('created_at', { ascending: false });

      if (search) {
        query = query.or(`name.ilike.%${search}%,sku.ilike.%${search}%`);
      }

      if (categoryFilter) {
        query = query.eq('category_id', categoryFilter);
      }

      if (statusFilter === 'active') {
        query = query.eq('is_active', true);
      } else if (statusFilter === 'inactive') {
        query = query.eq('is_active', false);
      } else if (statusFilter === 'out_of_stock') {
        query = query.eq('stock', 0);
      } else if (statusFilter === 'featured') {
        query = query.eq('is_featured', true);
      }

      const { data, error } = await query;

      if (error) throw error;

      setProducts(data || []);

      // Calcular stats
      const allProducts = data || [];
      setStats({
        total: allProducts.length,
        active: allProducts.filter(p => p.is_active).length,
        outOfStock: allProducts.filter(p => p.stock === 0).length,
        featured: allProducts.filter(p => p.is_featured).length
      });
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    const { data } = await supabase
      .from('shop_categories')
      .select('*')
      .order('name');

    setCategories(data || []);
  };

  const handleToggleActive = async (product) => {
    try {
      await supabase
        .from('shop_products')
        .update({ is_active: !product.is_active })
        .eq('id', product.id);

      fetchProducts();
    } catch (error) {
      console.error('Erro ao atualizar produto:', error);
    }
  };

  const handleToggleFeatured = async (product) => {
    try {
      await supabase
        .from('shop_products')
        .update({ is_featured: !product.is_featured })
        .eq('id', product.id);

      fetchProducts();
    } catch (error) {
      console.error('Erro ao atualizar produto:', error);
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Deseja excluir o produto "${product.name}"?`)) return;

    try {
      await supabase
        .from('shop_products')
        .delete()
        .eq('id', product.id);

      fetchProducts();
    } catch (error) {
      console.error('Erro ao excluir produto:', error);
    }
  };

  return (
    <PageContainer>
      <PageHeader>
        <h1>
          <FaBoxOpen /> Produtos
        </h1>
        <AddButton to="/admin/produtos/novo">
          <FaPlus /> Novo Produto
        </AddButton>
      </PageHeader>

      <Stats>
        <StatCard>
          <h4>Total</h4>
          <p>{stats.total}</p>
        </StatCard>
        <StatCard>
          <h4>Ativos</h4>
          <p>{stats.active}</p>
        </StatCard>
        <StatCard>
          <h4>Sem Estoque</h4>
          <p>{stats.outOfStock}</p>
        </StatCard>
        <StatCard>
          <h4>Destaques</h4>
          <p>{stats.featured}</p>
        </StatCard>
      </Stats>

      <Toolbar>
        <SearchBox>
          <FaSearch />
          <input
            type="text"
            placeholder="Buscar por nome ou SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchBox>

        <FilterSelect value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          <option value="">Todas categorias</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </FilterSelect>

        <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">Todos status</option>
          <option value="active">Ativos</option>
          <option value="inactive">Inativos</option>
          <option value="out_of_stock">Sem estoque</option>
          <option value="featured">Destaques</option>
        </FilterSelect>
      </Toolbar>

      {products.length === 0 && !loading ? (
        <EmptyState>
          <FaBoxOpen />
          <h3>Nenhum produto encontrado</h3>
          <p>Adicione seu primeiro produto para comecar a vender</p>
          <AddButton to="/admin/produtos/novo">
            <FaPlus /> Novo Produto
          </AddButton>
        </EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Produto</Th>
              <Th>Categoria</Th>
              <Th>Preco</Th>
              <Th>Estoque</Th>
              <Th>Status</Th>
              <Th>Acoes</Th>
            </tr>
          </thead>
          <tbody>
            {products.map(product => (
              <tr key={product.id}>
                <Td>
                  <ProductInfo>
                    <ProductImage
                      src={product.images?.[0] || 'https://placehold.co/48x48/f5f5f5/999?text=P'}
                      alt={product.name}
                    />
                    <div>
                      <ProductName>{product.name}</ProductName>
                      {product.sku && <ProductSku>SKU: {product.sku}</ProductSku>}
                    </div>
                  </ProductInfo>
                </Td>
                <Td>{product.category?.name || '-'}</Td>
                <Td>
                  <Price>{formatPrice(product.price)}</Price>
                </Td>
                <Td>
                  <Stock $low={product.stock <= 5}>
                    {product.stock} un
                  </Stock>
                </Td>
                <Td>
                  <StatusBadge $active={product.is_active}>
                    {product.is_active ? 'Ativo' : 'Inativo'}
                  </StatusBadge>
                </Td>
                <Td>
                  <Actions>
                    <ActionButton
                      onClick={() => handleToggleFeatured(product)}
                      title={product.is_featured ? 'Remover destaque' : 'Destacar'}
                      $warning={product.is_featured}
                    >
                      {product.is_featured ? <FaStar /> : <FaRegStar />}
                    </ActionButton>
                    <ActionButton
                      onClick={() => handleToggleActive(product)}
                      title={product.is_active ? 'Desativar' : 'Ativar'}
                    >
                      {product.is_active ? <FaEye /> : <FaEyeSlash />}
                    </ActionButton>
                    <ActionButton as={Link} to={`/admin/produtos/editar/${product.id}`} title="Editar">
                      <FaEdit />
                    </ActionButton>
                    <ActionButton $danger onClick={() => handleDelete(product)} title="Excluir">
                      <FaTrash />
                    </ActionButton>
                  </Actions>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </PageContainer>
  );
};

export default Products;
